import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { BarChart3 } from 'lucide-react';

const COLORS = ['#2A7B88', '#378ADD', '#BA7517', '#7C3AED', '#D4726A', '#2D8B5E', '#F59E0B'];

const fmtCurrency = (v) => {
  if (!v && v !== 0) return '—';
  if (v >= 1e7) return `₹${(v/1e7).toFixed(2).replace(/\.?0+$/, '')}Cr`;
  if (v >= 1e5) return `₹${(v/1e5).toFixed(2).replace(/\.?0+$/, '')}L`;
  return `₹${Math.round(v).toLocaleString('en-IN')}`;
};

/**
 * Channel Mix widget — shows split of daily sales by sub-category.
 *
 * Uses the same `outlet-detail` endpoint that powers the daily-sales drill-down
 * modal, so TDB's Online/Store/Swiggy/Zomato/Delivery split shows on the
 * outlet detail page automatically, just like on the division daily-sales tab.
 * Collapses silently if the outlet has no sub-amount data (e.g. older
 * hospitality outlets where sales are tracked in a single bucket).
 */
export default function ChannelMixWidget({ divisionId, outletName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!divisionId || !outletName) return;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(
          `/daily-sales/outlet-detail?division_id=${divisionId}&outlet=${encodeURIComponent(outletName)}`
        );
        setData(r.data);
      } catch (_) { setData(null); }
      setLoading(false);
    })();
  }, [divisionId, outletName]);

  if (loading) return null;
  const subTotals = data?.sub_totals || {};
  const rows = Object.entries(subTotals)
    .filter(([, v]) => v > 0)
    .sort(([, a], [, b]) => b - a);
  if (rows.length === 0) return null;

  const total = data.total || rows.reduce((a, [, v]) => a + v, 0);

  return (
    <div className="rounded-lg border p-4 bg-white" style={{ borderColor: '#E5E1DC' }} data-testid="outlet-channel-mix">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded flex items-center justify-center" style={{ backgroundColor: '#F7F5F2' }}>
            <BarChart3 size={14} style={{ color: '#2A7B88' }} />
          </div>
          <div>
            <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Channel Mix</p>
            <p className="text-[10px]" style={{ color: '#6B7280' }}>
              {rows.length} active channel{rows.length === 1 ? '' : 's'} · {data.entry_count || 0} entries
            </p>
          </div>
        </div>
        <p className="text-sm font-bold tabular-nums" style={{ color: '#1B2A4A' }} data-testid="outlet-channel-total">{fmtCurrency(total)}</p>
      </div>

      {/* Stacked proportion bar */}
      <div className="flex h-2 rounded-full overflow-hidden mb-3" style={{ backgroundColor: '#F3F4F6' }}>
        {rows.map(([k, v], i) => {
          const pct = (v / total) * 100;
          return <div key={k} style={{ width: `${pct}%`, backgroundColor: COLORS[i % COLORS.length] }} title={`${k}: ${pct.toFixed(1)}%`} />;
        })}
      </div>

      {/* Per-channel rows */}
      <div className="space-y-1.5">
        {rows.map(([k, v], i) => {
          const pct = total > 0 ? (v / total) * 100 : 0;
          return (
            <div key={k} className="flex items-center justify-between text-xs" data-testid={`outlet-channel-${k.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="flex items-center gap-2 flex-1">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                <span style={{ color: '#1B2A4A' }}>{k}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-semibold tabular-nums" style={{ color: '#1B2A4A' }}>{fmtCurrency(v)}</span>
                <span className="tabular-nums w-10 text-right" style={{ color: '#6B7280' }}>{pct.toFixed(1)}%</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
